import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { CalendarIcon } from '@/assets/icons/CalendarIcon';
import { MapPinIcon } from '@/assets/icons/MapPinIcon';
import { CheckIcon } from '@/assets/icons/CheckIcon';
import type { FeaturedEventProps } from '@/lib/types/events.types';

export interface FeaturedEventCardProps {
  event: FeaturedEventProps;
  /** Whether the current user is registered — green ✓ button when true */
  isRegistered?: boolean;
  /** Route path for the "View Details" link — omit on Dashboard (no navigation) */
  detailPath?: string;
  /** Router state for the View Details link (e.g. so the detail page can link back to the caller). */
  detailState?: unknown;
  /** Registers the user directly from the card. Without it no Register button shows. */
  onRegister?: () => void;
  /** Disables Register and shows progress while the registration request is in flight. */
  isRegistering?: boolean;
}

/** Hero card for the Featured Event slot — not clickable; only "View Details" navigates. */
export function FeaturedEventCard({
  event,
  isRegistered = false,
  detailPath,
  detailState,
  onRegister,
  isRegistering = false,
}: FeaturedEventCardProps) {
  return (
    // Container query, not a viewport breakpoint: the card sits in columns of
    // varying width (dashboard with or without its side column, the events
    // page), so it lays out by its own width. Side by side when there is room,
    // photo stacked on top when there is not. The photo keeps the uploaded
    // 16:9 ratio either way, never a fixed height that turns it portrait in a
    // narrow card (UX sheet v2).
    <div className="@container w-full">
      <div className="flex w-full flex-col gap-6 rounded-2xl border border-card-border bg-white p-5 shadow-sm @3xl:flex-row @3xl:items-center @3xl:gap-[60px] @3xl:p-7">
        {/* Left (or below the photo when narrow): content */}
        <div className="flex flex-1 flex-col gap-5 min-w-0">
          {/* "FEATURED EVENT" yellow badge */}
          <span className="inline-flex w-fit items-center rounded px-2.5 py-[5px] text-[0.6875rem] font-bold uppercase tracking-wider bg-featured-badge text-on-yellow leading-none">
            Featured Event
          </span>

          <div className="flex flex-col gap-4">
            <h3 className="text-[1.5rem] leading-[2rem] font-semibold tracking-[-0.42px] text-ink @3xl:text-[2rem] @3xl:leading-[2.625rem]">
              {event.title}
            </h3>

            {/* Meta rows */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2.5">
                <CalendarIcon size={16} className="text-green shrink-0" />
                <span className="text-[1rem] font-medium text-ink">{event.dateLabel}</span>
              </div>
              <div className="flex items-center gap-2.5">
                <MapPinIcon size={16} className="text-green shrink-0" />
                <span className="text-[1rem] font-medium text-ink">{event.location}</span>
              </div>
            </div>

            {event.description && (
              <p className="text-[0.9375rem] text-body leading-relaxed line-clamp-3">
                {event.description}
              </p>
            )}
          </div>

          {/* CTAs - View Details always present when detailPath is given.
            • Registered   → "✓ Registered" (green filled) + "View Details" (outlined)
            • Not registered → "Register" (registers right here) + "View Details" */}
          {detailPath && (
            <div className="flex items-center gap-3">
              {isRegistered ? (
                <span className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-[0.9375rem] font-semibold bg-green text-white">
                  <CheckIcon />
                  Registered
                </span>
              ) : (
                onRegister && (
                  <Button
                    variant="green"
                    onClick={onRegister}
                    disabled={isRegistering}
                    className="rounded-lg! px-5! py-2.5! text-[0.9375rem]!"
                  >
                    {isRegistering ? 'Registering…' : 'Register'}
                  </Button>
                )
              )}
              <Link
                to={detailPath}
                state={detailState}
                className="rounded-lg px-5 py-2.5 text-[0.9375rem] font-semibold border border-ink text-ink hover:bg-page-bg transition-colors"
              >
                View Details
              </Link>
            </div>
          )}
        </div>

        {/* Right (or on top when narrow): cover image */}
        <div className="order-first aspect-video w-full shrink-0 overflow-hidden rounded-2xl bg-input-disabled @3xl:order-none @3xl:w-[44%] @3xl:max-w-[712px]">
          {event.imageUrl ? (
            <img src={event.imageUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-surface-from to-surface-to" />
          )}
        </div>
      </div>
    </div>
  );
}
